/**
 * SingleMessageView.js
 * Talkroute
 *
 * Created by Vel Genov on 4/17/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Linkify from "react-linkify";

import { TextMessageData } from "../../services/dataClasses/TextMessageData";
import uiString from "../../constants/uiString";
import validateArray from "../../helpers/arrayValidator";
import PictureMessageView from "./PictureMessageView";
import { TextMessageAttachment } from "../../services/dataClasses/TextMessageAttachment";

interface ISingleMessageViewProps {
  message?: TextMessageData;
  conversationId?: string;
  shouldShowDateStamp: boolean;
  allAttachments: any;
}

/**
 * Display a single conversation message
 *
 */
class SingleMessageView extends React.Component<ISingleMessageViewProps, any> {
  static defaultProps = {
    shouldShowDateStamp: false,
    allAttachments: {},
  };

  /**
   * Draw a single message
   */
  showMessage = () => {
    const { message } = this.props;
    if (!message) return;
    const { body, userEmail, id: messageId } = message;
    const isIncoming = message.isIncoming();
    if (!body) {
      return null;
    }

    const linkifyMessageBody = <Linkify>{body}</Linkify>;

    const messageBody = <div className="message">{linkifyMessageBody}</div>;

    if (isIncoming === true) {
      return this.showIncomingMessage(messageBody, messageId);
    }

    return this.showOutgoingMessage(messageBody, userEmail, messageId);
  };

  /**
   * Draw an incoming message
   *
   * @param messageBody the body of the message to draw
   */
  showIncomingMessage = (messageBody, key) => {
    return (
      <div key={key} className="incoming messages text-normal">
        {messageBody}
      </div>
    );
  };

  /**
   * Draw an outgoing message
   *
   * @param messageBody the body of the message to draw
   */
  showOutgoingMessage = (messageBody, userEmail, key) => {
    return (
      <div key={key} className="outgoing messages text-normal-white">
        {messageBody} {this.showUserEmail(userEmail)}
      </div>
    );
  };

  /**
   * Process message attachments if there are any
   */
  processAttachments = () => {
    const { message, conversationId, allAttachments } = this.props;
    if (!message) return;
    const {
      id: messageId,
      attachments: messageAttachments,
      userEmail,
    } = message;
    const isIncoming = message.isIncoming();

    if (validateArray(messageAttachments)) {
      const processedAttachments = messageAttachments.map((attachment) => {
        const { id: attachmentId } = attachment;
        const currentAttachment = allAttachments[attachmentId];
        if (currentAttachment) {
          const messageBody = (
            <PictureMessageView
              attachment={currentAttachment}
              filePath={currentAttachment.filePath}
              errorLoading={currentAttachment.errorLoading}
              messageId={messageId}
              conversationId={conversationId}
            />
          );

          if (!messageBody) {
            return null;
          }

          if (isIncoming === true) {
            return this.showIncomingMessage(messageBody, attachmentId);
          }

          return this.showOutgoingMessage(messageBody, userEmail, attachmentId);
        }
        return null;
      });

      return processedAttachments;
    }

    return null;
  };

  /**
   * If a user email is provided, display it
   *
   * @param userEmail to userEmail to display or undefined
   */
  showUserEmail = (userEmail) => {
    if (userEmail) {
      const emailString = `${uiString.BY}: ${userEmail}`;
      return <div className="useremail text-small">{emailString}</div>;
    }
    return null;
  };

  /**
   * Show the date stamp for a message
   */
  showDateStamp = () => {
    const { message, shouldShowDateStamp } = this.props;
    const dateStamp = message?.getFormattedDateAndTime();

    if (shouldShowDateStamp === true) {
      return <div className="datestamp text-normal">{dateStamp}</div>;
    }

    return null;
  };

  render() {
    return (
      <div>
        {this.showDateStamp()}
        {this.showMessage()}
        {this.processAttachments()}
      </div>
    );
  }
}

/**
 * Map props to a redux state
 * @param {*} state
 */
const mapStateToProps = (state) => ({
  allAttachments: state.conversations.attachments,
});

export default connect(mapStateToProps, {})(SingleMessageView);
